import logger from '../../utils/secureLogger';
import { fetchAuthSession } from "@aws-amplify/auth";

class HealthOpsChatWebSocketService {
  constructor() {
    this.ws = null;
    this.wsUrl = import.meta.env.VITE_WS_URL_HEALTH_AGENT;
    this.authToken = null;
    this.listeners = new Map();
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 2000; 
    this.connectionTimeout = null;
    this.messageQueue = [];
    this.pendingMessages = new Map(); 
    this.lastSendTime = null; 
    this.heartbeatInterval = null; 
    this.heartbeatIntervalTime = 30000; 
  }

 
  connect() {
    return new Promise(async (resolve, reject) => {
      if (this.isConnected) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        return;
      }

      if (!this.wsUrl) {
        const error = 'VITE_WS_URL_HEALTH_AGENT no está configurado en .env';
        this.emit('error', { message: error, type: 'configuration' });
        reject(new Error(error));
        return;
      }

      try {
        try {
          const session = await fetchAuthSession({ forceRefresh: false });
          
          if (!session.tokens?.idToken) {
            throw new Error('No authentication token available');
          }

          this.authToken = session.tokens.idToken.toString();
        } catch (authError) {
          this.emit('error', { 
            message: 'Authentication failed. Please login again.', 
            type: 'authentication',
            originalError: authError 
          });
          reject(new Error('Authentication token not available'));
          return;
        }

        const wsUrlWithAuth = `${this.wsUrl}?token=${encodeURIComponent(this.authToken)}`;
        this.isConnecting = true;
        this.connectionTimeout = setTimeout(() => {
          if (!this.isConnected) {
            this.handleConnectionError(new Error('Connection timeout'));
            reject(new Error('Connection timeout'));
          }
        }, 10000);

        this.ws = new WebSocket(wsUrlWithAuth);

        this.ws.onopen = () => {
          clearTimeout(this.connectionTimeout);
          this.isConnected = true;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 2000; 
          
          this.emit('connect', { timestamp: new Date() });
          
          this.processMessageQueue();
          
          this.startHeartbeat();
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.ws.onerror = (error) => {
          clearTimeout(this.connectionTimeout);
          this.isConnecting = false;
        };

        this.ws.onclose = (event) => {
          clearTimeout(this.connectionTimeout);
          const wasConnected = this.isConnected;
          
          setTimeout(() => {
            this.isConnected = false;
            this.isConnecting = false;
            
            this.stopHeartbeat();
            
            const timeSinceLastSend = this.lastSendTime ? Date.now() - this.lastSendTime : null;
            const closedAfterSend = timeSinceLastSend !== null && timeSinceLastSend < 5000; // Menos de 5 segundos
            const hasPendingMessages = this.pendingMessages.size > 0;
            
            logger.debug('🔌 WebSocket cerrado:', { 
              code: event.code, 
              reason: event.reason, 
              wasClean: event.wasClean,
              wasConnected,
              closedAfterSend,
              timeSinceLastSend,
              pendingMessages: this.pendingMessages.size
            });

            if (hasPendingMessages) {
              
              this.emit('disconnect', {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                timestamp: new Date(),
                shouldResetSession: false, 
                hasPendingMessages: true
              });

              setTimeout(() => {
                this.connect().catch(error => {
                  });
              }, 1000); 
              
            } else {
              
              this.emit('disconnect', {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                timestamp: new Date(),
                shouldResetSession: wasConnected && !event.wasClean
              });

              if (wasConnected && !event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
                this.scheduleReconnect();
              } else if (!wasConnected && !event.wasClean) {
                reject(new Error('Failed to establish WebSocket connection'));
              }
            }
          }, 100); 
        };

      } catch (error) {
        clearTimeout(this.connectionTimeout);
        this.isConnecting = false;
        this.emit('error', { message: error.message, type: 'connection' });
        reject(error);
      }
    });
  }


  disconnect() {
    this.reconnectAttempts = this.maxReconnectAttempts; 
    
    
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect'); 
      this.ws = null;
    }
    
    this.isConnected = false;
    this.isConnecting = false;
    this.messageQueue = [];
    
  }


  sendMessage(message, sessionId) {
    if (!message || !message.trim()) {
      return;
    }

    if (!sessionId) {
      sessionId = crypto.randomUUID();
    }

    const payload = {
      action: 'message',
      message: message.trim(),
      sessionId: sessionId
    };

    const isActuallyConnected = this.isConnected && 
                                 this.ws && 
                                 this.ws.readyState === WebSocket.OPEN;

    

    if (!isActuallyConnected) {
      this.messageQueue.push(payload);
      
      if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
        this.isConnected = false;
      }
      
      if (!this.isConnecting) {
       this.connect().catch(error => {
          });
      } else {
        }
      return;
    }

    try {
      const payloadString = JSON.stringify(payload);
      
      this.ws.send(payloadString);
      
      this.lastSendTime = Date.now();
      this.pendingMessages.set(sessionId, {
        payload,
        sentAt: this.lastSendTime
      });
      
      this.emit('messageSent', {
        message,
        sessionId,
        timestamp: new Date()
      });
      
    } catch (error) {
      this.messageQueue.push(payload);
      this.isConnected = false;
      
      this.emit('error', {
        message: 'Failed to send message',
        type: 'send',
        originalError: error
      });
      
      if (!this.isConnecting) {
        this.connect().catch(() => {
        });
      }
    }
  }

  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      
      
      
      if (data.type === 'complete') {
        const { content } = data;
        
        if (content && content.status === 'success') {
          logger.debug('✅ Respuesta exitosa del agente');
          
          if (content.session_id && this.pendingMessages.has(content.session_id)) {
            this.pendingMessages.delete(content.session_id);
          }
          
          this.emit('message', {
            type: 'agent',
            content: content.message,
            sessionId: content.session_id,
            timestamp: new Date(),
            raw: data
          });
        } else {
          logger.error('❌ Respuesta con error del agente:', content);
          
           if (content?.session_id && this.pendingMessages.has(content.session_id)) {
            this.pendingMessages.delete(content.session_id);
          }
          
          this.emit('error', {
            message: content?.message || 'Unknown error from agent',
            type: 'agent',
            sessionId: content?.session_id
          });
        }
      }
      else if (data.type === 'error') {
        logger.error('❌ Error del agente:', data.message);
        this.emit('error', {
          message: data.message || 'Unknown error',
          type: 'agent',
          raw: data
        });
      }
      else {
        this.emit('unknown', data);
      }
      
    } catch (error) {
      this.emit('error', {
        message: 'Failed to parse message from server',
        type: 'parse',
        originalError: error
      });
    }
  }

  handleConnectionError(error) {
    this.isConnected = false;
    this.isConnecting = false;
    
    this.emit('error', {
      message: error.message || 'Connection error',
      type: 'connection',
      originalError: error
    });
  }
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('maxReconnectAttemptsReached', {
        attempts: this.reconnectAttempts
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Backoff exponencial
    
    
    this.emit('reconnecting', {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delay
    });

    setTimeout(() => {
      this.connect().catch(error => {
        });
    }, delay);
  }

  processMessageQueue() {
    if (this.messageQueue.length === 0) return;
    
    
    while (this.messageQueue.length > 0) {
      const payload = this.messageQueue.shift();
      try {
        this.ws.send(JSON.stringify(payload));
      } catch (error) {
        this.messageQueue.unshift(payload);
        break;
      }
    }
    }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
      }
    });
  }

  getConnectionStatus() {
    if (this.isConnected) return 'connected';
    if (this.isConnecting) return 'connecting';
    return 'disconnected';
  }
  isWebSocketConnected() {
    return this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  getDebugInfo() {
    return {
      wsUrl: this.wsUrl,
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      messageQueueLength: this.messageQueue.length,
      readyState: this.ws ? this.ws.readyState : null,
      heartbeatActive: !!this.heartbeatInterval,
      hasAuthToken: !!this.authToken
    };
  }

  /**
   * Refrescar el token de autenticación
   * Útil cuando el token expira y necesitas reconectar
   */
  async refreshAuthToken() {
    try {
      logger.debug('🔄 Refreshing authentication token...');
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (!session.tokens?.idToken) {
        throw new Error('No authentication token available after refresh');
      }

      this.authToken = session.tokens.idToken.toString();
      logger.debug('✅ Authentication token refreshed successfully');
      return this.authToken;
    } catch (error) {
      logger.error('❌ Failed to refresh authentication token:', error);
      throw error;
    }
  }

  /**
   * Reconectar con un token fresco
   * Útil cuando la conexión falla por token expirado
   */
  async reconnectWithFreshToken() {
    try {
      logger.debug('🔄 Reconnecting with fresh token...');
      
      // Desconectar primero
      if (this.ws) {
        this.ws.close(1000, 'Reconnecting with fresh token');
      }
      
      this.isConnected = false;
      this.isConnecting = false;
      
      // Refrescar token y reconectar
      await this.refreshAuthToken();
      await this.connect();
      
      logger.debug('✅ Reconnected successfully with fresh token');
    } catch (error) {
      logger.error('❌ Failed to reconnect with fresh token:', error);
      throw error;
    }
  }

  startHeartbeat() {
    // Limpiar cualquier heartbeat existente
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          // Enviar un ping nativo de WebSocket si está disponible
          // Si el servidor no soporta ping/pong, esto mantendrá la conexión TCP viva
          if (typeof this.ws.ping === 'function') {
            this.ws.ping();
             } else {
            }
        } catch (error) {
          this.stopHeartbeat();
        }
      } else {
         this.stopHeartbeat();
      }
    }, this.heartbeatIntervalTime);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

const healthOpsChatWebSocketService = new HealthOpsChatWebSocketService();

export default healthOpsChatWebSocketService;

export { HealthOpsChatWebSocketService };
